import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Button, Chip } from '@leadsales/design-system/react';
import classNames from 'classnames';
import { ReactElement, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AlertCard from 'src/components/AlertCenter/AlertCard.component';
import CardBrand from '../CardSelector/CardBrand.component';
import PaymentMethodRoleBadge from '../PaymentMethodRoles/PaymentMethodRoleBadge.component';
import { eCardBrand } from '../../SubscriptionManager.enums';
import { tPaymentMethod } from '../../SubscriptionManager.types';
import {
  PAST_DUE_HOSTED_OPTION,
  isExpiredCard,
  tPastDueDebtInvoice,
  tPastDueDecline,
  tPastDuePaymentView,
  tPastDueTransferInstructions,
} from './PastDuePaymentZone.types';
import './PastDuePaymentZone.styles.scss';

const BRAND_NAMES: Record<string, string> = {
  [eCardBrand.VISA]: 'Visa',
  [eCardBrand.MASTERCARD]: 'Mastercard',
  [eCardBrand.AMERICAN_EXPRESS]: 'American Express',
  amex: 'American Express',
};

/** «en dólares», not «en USD» (Fanny, 21-oct). */
const CURRENCY_NAMES: Record<string, string> = {
  usd: 'dólares',
  mxn: 'pesos',
};

export function brandName(brand: string | null | undefined): string {
  if (!brand) return '';
  return BRAND_NAMES[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

/**
 * «18 de octubre», without the year (Fanny, 21-oct) — unless the date is from
 * another year: M-16 includes invoices from months or years back (23-oct).
 */
function formatDeclineDate(date: Date): string {
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString(
    'es-MX',
    sameYear
      ? { day: 'numeric', month: 'long' }
      : { day: 'numeric', month: 'long', year: 'numeric' },
  );
}

/**
 * The card the decline points at, when it is still saved. An unfinished bank
 * authentication is not a refusal (M-21, MO-8): it keeps its notice under the
 * card and allows a retry, but the card carries no «Rechazada» mark (Fanny,
 * 21-oct). Our own error points at no card.
 */
function declinedCardId(decline: tPastDueDecline | null): string | null {
  if (!decline || decline.group === 'our_error') return null;
  return decline.paymentMethodId;
}

/**
 * The «Rechazada» mark is for what the bank did refuse. A processing error, a
 * temporary bank error or an unfinished authentication keep their notice under
 * the card, without the mark (Fanny, 21-oct: «la marca queda para lo que el
 * banco sí rechazó»).
 */
const UNMARKED_GROUPS = new Set([
  'authentication_incomplete',
  'processing_error',
  'temporary_bank_error',
  'our_error',
]);

/** Whether this saved card is the one the decline points at. */
export function isDeclinedCard(
  decline: tPastDueDecline | null,
  paymentMethodId: string | null,
): boolean {
  return (
    paymentMethodId !== null && declinedCardId(decline) === paymentMethodId
  );
}

/**
 * The first line of a decline shown inside its card (Fanny, 23-oct-2026): the
 * mark and the reason in one sentence, «Rechazada el 20 de octubre por fondos
 * insuficientes». What the bank did not refuse (M-21) keeps its own title,
 * without «Rechazada».
 */
function useDeclineHeadline(decline: tPastDueDecline): string {
  const { t } = useTranslation('settings/SubscriptionManager');
  const key = `pastDuePayment.decline.${decline.group}`;
  if (UNMARKED_GROUPS.has(decline.group)) return t(`${key}.title`);
  const reason =
    decline.group === 'brand_not_supported'
      ? decline.cardBrand && decline.currency
        ? t(`${key}.reason`, {
            brand: brandName(decline.cardBrand),
            currency:
              CURRENCY_NAMES[decline.currency.toLowerCase()] ??
              decline.currency.toUpperCase(),
          })
        : t(`${key}.reasonWithoutDetail`)
      : t(`${key}.reason`);
  return decline.declinedAt && !isSameLocalDay(decline.declinedAt, new Date())
    ? t('pastDuePayment.declinedOnBecause', {
        date: formatDeclineDate(decline.declinedAt),
        reason,
      })
    : t('pastDuePayment.declinedTodayBecause', { reason });
}

type tCardDeclineReasonProps = {
  decline: tPastDueDecline;
  /** Inside a partial charge the body drops «No se cobró nada» (RET-2175). */
  partial: boolean;
  /** In the past-due purchase modal: «No hicimos ningún cambio en tu suscripción.» (M-18, M-19). */
  extraLine?: string;
  /** Under the modal's card selector, as the field's error, instead of inside a card box. */
  asFieldError?: boolean;
};

/**
 * M-21 — the decline of a saved card, said inside that card's box (Fanny,
 * 23-oct-2026: «que el motivo de rechazo esté más vinculado con la tarjeta»).
 * Not a second box that looks like another card: the headline carries the mark
 * and the reason, the body says what to do. In the modal, the same text sits
 * under the card selector as the field's error.
 */
export function CardDeclineReason(
  props: tCardDeclineReasonProps,
): ReactElement {
  const { t } = useTranslation('settings/SubscriptionManager');
  const { decline } = props;
  const key = `pastDuePayment.decline.${decline.group}`;
  const headline = useDeclineHeadline(decline);
  // M-21: a card just added here and refused — both card slots went back. When
  // a slot could not be put back, the screen never says the plan's card did not
  // change (MO-7), and says what to do from here instead (Fanny, 21-oct).
  const restoredLine =
    decline.cardsRestored === 'restored'
      ? t('pastDuePayment.cardNotChanged')
      : decline.cardsRestored === 'not_restored'
        ? t('pastDuePayment.cardNotRestored')
        : null;
  // At the retry cap the body says why the same card cannot be tried again
  // and what to do instead (M-21, Fanny, 23-oct-2026).
  const body = [
    decline.retryLimit
      ? t(`pastDuePayment.retryLimit.${decline.retryLimit}`)
      : t(props.partial ? `${key}.bodyPartial` : `${key}.body`),
    restoredLine,
    props.extraLine,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classNames('PastDuePaymentZone__reason', {
        'PastDuePaymentZone__reason--field': props.asFieldError,
      })}
      role="alert"
      data-testid="past-due-decline-notice"
    >
      {/* No icon: an outlined circle next to the radios read as another radio
          (Fanny, 23-oct-2026). The red border and the red headline say it. */}
      <div className="PastDuePaymentZone__reasonText">
        <p className="PastDuePaymentZone__reasonTitle">{headline}</p>
        <p className="PastDuePaymentZone__reasonBody">{body}</p>
      </div>
    </div>
  );
}

/**
 * ONE rule for the one pay button, shared by the payment zone and the past-due
 * purchase modal (Fanny, 21-oct-2026): «Pagar …» on a card without a decline,
 * «Reintentar con Visa ···4242» on the refused card when the reason allows a
 * retry (M-21), and «Elige otra tarjeta», off, when it does not. After an error
 * on our side the button stays off until the page is reloaded: the payment may
 * have gone through (Fanny, 21-oct).
 */
export function usePastDuePayButton(view: tPastDuePaymentView): {
  label: string;
  disabled: boolean;
} {
  const { t } = useTranslation('settings/SubscriptionManager');
  const decline = view.decline;
  const selected = view.paymentMethods.find(
    (pm) => pm.paymentMethodId === view.selectedPaymentMethodId,
  );
  const payLabel = t('pastDuePayment.pay', { amount: view.amountOwed ?? '' });
  // After our own error nothing pays until reload, the payment link included:
  // the payment may have gone through (M-21).
  if (decline?.group === 'our_error') {
    return { label: payLabel, disabled: true };
  }
  // «Pagar con un enlace de pago»: the button opens it (Fanny, 23-oct-2026).
  if (
    view.selectedPaymentMethodId === PAST_DUE_HOSTED_OPTION &&
    view.hostedPaymentUrl
  ) {
    return { label: t('pastDuePayment.hosted.go'), disabled: false };
  }
  const selectedIsDeclined =
    selected !== undefined &&
    declinedCardId(decline) === selected.paymentMethodId;
  if (selectedIsDeclined && decline) {
    return decline.retryAllowed
      ? {
          label: t('pastDuePayment.retryWith', {
            brand: brandName(selected?.brand),
            last4: selected?.lastDigits,
          }),
          disabled: false,
        }
      : { label: t('pastDuePayment.chooseAnother'), disabled: true };
  }
  return {
    label: payLabel,
    disabled: !selected || !view.amountOwed,
  };
}

type tDeclineNoticeProps = {
  decline: tPastDueDecline;
  /** Inside «Se cobraron … de …» the body drops «No se cobró nada» (RET-2175). */
  partial: boolean;
  /** In the past-due purchase modal: «No hicimos ningún cambio en tu suscripción.» (M-18, M-19). */
  extraLine?: string;
  /** The refused card is not in the list: the title names it. */
  namesCard?: boolean;
};

/**
 * M-21 — a decline with no saved card to sit in: the refused card was deleted
 * since, or the error was ours. It goes on top of the zone, before the cards,
 * because it is about the debt's last attempt, not about a card in the list
 * (Fanny, 23-oct-2026). The title says what happened and why; the body, what
 * to do. Never a toast: it stays in view while the customer picks a card.
 */
export function DeclineNotice(props: tDeclineNoticeProps): ReactElement {
  const { t } = useTranslation('settings/SubscriptionManager');
  const { decline } = props;
  const key = `pastDuePayment.decline.${decline.group}`;
  const title =
    decline.group === 'brand_not_supported'
      ? decline.cardBrand && decline.currency
        ? t(`${key}.title`, {
            brand: brandName(decline.cardBrand),
            currency:
              CURRENCY_NAMES[decline.currency.toLowerCase()] ??
              decline.currency.toUpperCase(),
          })
        : t(`${key}.titleWithoutDetail`)
      : decline.group === 'expired_card' &&
          props.namesCard &&
          decline.cardBrand &&
          decline.cardLast4
        ? // The card is not on screen (deleted since): the title names it, or
          // it reads as the card that is (Fanny, 21-oct).
          t(`${key}.titleWithCard`, {
            brand: brandName(decline.cardBrand),
            last4: decline.cardLast4,
          })
        : t(`${key}.title`);
  const body = t(props.partial ? `${key}.bodyPartial` : `${key}.body`);
  // M-21: a card just added here and refused — both card slots went back. When
  // a slot could not be put back, the screen never says the plan's card did not
  // change (MO-7), and says what to do from here instead (Fanny, 21-oct).
  const restoredLine =
    decline.cardsRestored === 'restored'
      ? t('pastDuePayment.cardNotChanged')
      : decline.cardsRestored === 'not_restored'
        ? t('pastDuePayment.cardNotRestored')
        : null;

  return (
    <AlertCard
      variant="error"
      icon={
        decline.group === 'our_error' ? ExclamationTriangleIcon : XCircleIcon
      }
      title={title}
      descriptions={[body, restoredLine, props.extraLine].filter(
        (line): line is string => Boolean(line),
      )}
      role="alert"
      className="PastDuePaymentZone__notice"
      dataAttributes={{ 'data-testid': 'past-due-decline-notice' }}
    />
  );
}

type tCardOptionProps = {
  paymentMethod: tPaymentMethod;
  name: string;
  checked: boolean;
  disabled: boolean;
  /** This card's decline, shown inside its box; `null` when it has none or while the bank confirms. */
  decline: tPastDueDecline | null;
  partial: boolean;
  onSelect: (paymentMethodId: string) => void;
};

/** «04/28», as printed on the card; `null` without a month or year. */
function formatCardExpiry(card: {
  month?: number | null;
  year?: number | null;
}): string | null {
  if (!card.month || !card.year) return null;
  return `${String(card.month).padStart(2, '0')}/${String(card.year).slice(-2)}`;
}

function CardOption(props: tCardOptionProps): ReactElement {
  const { paymentMethod, decline } = props;
  const { t } = useTranslation('settings/SubscriptionManager');
  // A sure decline that counts toward the retry cap: marked and never
  // selectable (M-21, Fanny, 24-oct-2026).
  const expired = isExpiredCard(paymentMethod);
  const expiry = formatCardExpiry(paymentMethod);

  return (
    <div
      className={classNames('PastDuePaymentZone__cardBlock', {
        'PastDuePaymentZone__cardBlock--checked': props.checked,
        // Red only while it is the chosen one: once another card is picked the
        // reason stays, but the box goes back to grey (Fanny, 24-oct-2026).
        'PastDuePaymentZone__cardBlock--declined':
          decline !== null && props.checked,
        'PastDuePaymentZone__cardBlock--expired': expired,
      })}
    >
      <label
        className="PastDuePaymentZone__card"
        data-testid={`past-due-card-${paymentMethod.paymentMethodId}`}
      >
        <input
          type="radio"
          className="radio radio-primary radio-sm"
          name={props.name}
          value={paymentMethod.paymentMethodId}
          checked={props.checked}
          disabled={props.disabled || expired}
          onChange={() => props.onSelect(paymentMethod.paymentMethodId)}
        />
        <span className="PastDuePaymentZone__cardBrand" aria-hidden="true">
          <CardBrand brand={paymentMethod.brand} />
        </span>
        <span className="PastDuePaymentZone__cardText">
          {/* The expiry beside the brand and last digits, as printed on the
              card, and the card's tags after it, each in a column shared by
              every card (Fanny, 24-oct-2026): two cards of the same brand read
              apart, and one close to expiring shows before the charge. */}
          <span className="PastDuePaymentZone__cardName">
            <span className="PastDuePaymentZone__cardDigits">
              {brandName(paymentMethod.brand)} ···{paymentMethod.lastDigits}
            </span>
            {expiry ? (
              <span
                className="PastDuePaymentZone__cardExpiry"
                data-testid="past-due-card-expiry"
              >
                {t(
                  expired
                    ? 'pastDuePayment.expiredOn'
                    : 'pastDuePayment.expires',
                  {
                    date: expiry,
                  },
                )}
              </span>
            ) : null}
          </span>
          <span className="PastDuePaymentZone__cardTags">
            <PaymentMethodRoleBadge
              paymentMethodId={paymentMethod.paymentMethodId}
            />
            {expired ? (
              // The same «Vencida» as Ajustes and «Métodos de pago» (MO-9):
              // one color, so it reads as one state (revision 4, M18).
              <Chip
                color="warning"
                variant="outlined"
                size="sm"
                className="CardExpiryMark"
                data-testid="past-due-card-expired"
              >
                {t('pastDuePayment.expired')}
              </Chip>
            ) : null}
          </span>
        </span>
      </label>
      {decline ? (
        <CardDeclineReason decline={decline} partial={props.partial} />
      ) : null}
    </div>
  );
}

const PERIOD_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
};

function formatPeriodDate(date: Date): string {
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString(
    'es-MX',
    sameYear ? PERIOD_FORMAT : { ...PERIOD_FORMAT, year: 'numeric' },
  );
}

function formatPeriod(start: Date | null, end: Date | null): string | null {
  if (!start) return null;
  const from = formatPeriodDate(start);
  return end ? `${from} – ${formatPeriodDate(end)}` : from;
}

/**
 * M-16 — what «Lo que debes» is made of, as granular as Stripe gives it
 * (Fanny, 23-oct-2026): one block per open invoice, with each line (concept,
 * quantity × unit price, amount and period) and the invoice's total, what was
 * paid and what is left. Folded under the amount; the Owner and the Admin see
 * it, as the invoice link of M-20. Old invoices are in it too, with their
 * date, never hidden (M-16, M-17). «Ver factura en PDF» opens the PDF, never
 * the hosted page with its own pay button.
 */
function DebtBreakdown(props: {
  invoices: tPastDueDebtInvoice[];
  planName: string;
}): ReactElement | null {
  const { t } = useTranslation('settings/SubscriptionManager');
  const [open, setOpen] = useState(false);
  const listId = useId();
  if (props.invoices.length === 0) return null;
  const concept = (line: tPastDueDebtInvoice['lines'][number]) =>
    t(`pastDuePayment.breakdown.kind.${line.kind}`, {
      plan: props.planName,
      name: line.name ?? '',
    });
  const quantity = (line: tPastDueDebtInvoice['lines'][number]) => {
    if (line.quantity === null) return null;
    return line.unitAmount && line.kind !== 'seatsIncluded'
      ? t('pastDuePayment.breakdown.quantityTimesUnit', {
          quantity: line.quantity,
          unit: line.unitAmount,
        })
      : String(line.quantity);
  };

  return (
    <div className="PastDuePaymentZone__breakdown">
      <Button
        color="ghost"
        size="sm"
        className="PastDuePaymentZone__breakdownToggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={listId}
        data-testid="past-due-breakdown-toggle"
      >
        {t(
          open
            ? 'pastDuePayment.breakdown.hide'
            : 'pastDuePayment.breakdown.show',
        )}
      </Button>
      {open ? (
        <div id={listId} className="PastDuePaymentZone__invoices">
          {props.invoices.map((invoice) => (
            <section
              key={invoice.invoiceId}
              className="PastDuePaymentZone__invoice"
            >
              <header className="PastDuePaymentZone__invoiceHeader">
                <h3 className="PastDuePaymentZone__invoiceTitle">
                  {t('pastDuePayment.breakdown.invoiceOf', {
                    date: formatDeclineDate(invoice.issuedAt),
                  })}
                </h3>
                {invoice.invoicePdfUrl ? (
                  <a
                    href={invoice.invoicePdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="PastDuePaymentZone__breakdownLink"
                  >
                    {t('pastDuePayment.breakdown.viewInvoice')}
                  </a>
                ) : null}
              </header>
              <table className="PastDuePaymentZone__invoiceLines">
                <tbody>
                  {invoice.lines.map((line, index) => {
                    // The period once per group of lines that share it: the
                    // seats of a renewal cover the plan's same period.
                    const period = formatPeriod(
                      line.periodStart,
                      line.periodEnd,
                    );
                    const previous =
                      index > 0 ? invoice.lines[index - 1] : null;
                    const samePeriodAsPrevious =
                      previous !== null &&
                      formatPeriod(previous.periodStart, previous.periodEnd) ===
                        period;
                    return (
                      // The index as key: lines have no id of their own and never reorder.
                      <tr key={index}>
                        <th scope="row">
                          <span className="PastDuePaymentZone__breakdownConcept">
                            {concept(line)}
                          </span>
                          {period && !samePeriodAsPrevious ? (
                            <span className="PastDuePaymentZone__breakdownMeta">
                              {period}
                            </span>
                          ) : null}
                        </th>
                        <td className="PastDuePaymentZone__breakdownQuantity">
                          {quantity(line)}
                        </td>
                        <td className="PastDuePaymentZone__breakdownAmount">
                          {line.amount}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row" colSpan={2}>
                      {t('pastDuePayment.breakdown.total')}
                    </th>
                    <td className="PastDuePaymentZone__breakdownAmount">
                      {invoice.total}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" colSpan={2}>
                      {t('pastDuePayment.breakdown.paid')}
                    </th>
                    <td className="PastDuePaymentZone__breakdownAmount">
                      {invoice.amountPaid}
                    </td>
                  </tr>
                  <tr className="PastDuePaymentZone__invoiceRemaining">
                    <th scope="row" colSpan={2}>
                      {t('pastDuePayment.breakdown.remaining')}
                    </th>
                    <td className="PastDuePaymentZone__breakdownAmount">
                      {invoice.amountRemaining}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </section>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** «0021 8065 0612 3456 70»: easier to read aloud and to check against the bank's screen. */
function groupClabe(clabe: string): string {
  return clabe.replace(/(\d{4})(?=\d)/g, '$1 ');
}

/**
 * M-20 — manual collection pays by bank transfer, so that is the main way to
 * pay in the zone (Fanny, 23-oct-2026): the customer's own Stripe account, the
 * total to send and a copy button, without leaving the app for Stripe's invoice
 * page. Stripe applies the transfer to the open invoices by itself when it
 * arrives, oldest first, so one transfer of the total pays all of them.
 */
function TransferBlock(props: {
  instructions: tPastDueTransferInstructions | null;
  amount: string | null;
  /** The invoice number, with ONE open invoice: Stripe applies the transfer to it by reference (M-20). */
  reference: string | null;
  /** Part already arrived as cash balance: the amount to transfer is what is missing (M-20, 24-oct-2026). */
  received: { received: string; missing: string } | null;
}): ReactElement {
  const { t } = useTranslation('settings/SubscriptionManager');
  const [copied, setCopied] = useState(false);
  const titleId = useId();
  const { instructions } = props;
  const copy = async () => {
    if (!instructions) return;
    try {
      await navigator.clipboard.writeText(instructions.clabe);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Without clipboard access the CLABE is still on screen to copy by hand.
    }
  };

  return (
    <section className="PastDuePaymentZone__transfer" aria-labelledby={titleId}>
      <h3 id={titleId} className="PastDuePaymentZone__cardsTitle">
        {t('pastDuePayment.transfer.title')}
      </h3>
      {instructions ? (
        <>
          <dl
            className="PastDuePaymentZone__transferData"
            data-testid="past-due-transfer-data"
          >
            <dt>{t('pastDuePayment.transfer.bank')}</dt>
            <dd>{instructions.bankName}</dd>
            <dt>{t('pastDuePayment.transfer.clabe')}</dt>
            <dd className="PastDuePaymentZone__transferClabe">
              <span>{groupClabe(instructions.clabe)}</span>
              <Button
                color="ghost"
                size="sm"
                onClick={copy}
                aria-live="polite"
                data-testid="past-due-copy-clabe"
              >
                {t(
                  copied
                    ? 'pastDuePayment.transfer.copied'
                    : 'pastDuePayment.transfer.copy',
                )}
              </Button>
            </dd>
            <dt>{t('pastDuePayment.transfer.holder')}</dt>
            <dd>{instructions.accountHolderName}</dd>
            <dt>{t('pastDuePayment.transfer.amount')}</dt>
            <dd className="PastDuePaymentZone__transferAmount">
              {props.received?.missing ?? props.amount}
            </dd>
            {props.reference ? (
              <>
                <dt>{t('pastDuePayment.transfer.reference')}</dt>
                <dd>{props.reference}</dd>
              </>
            ) : null}
          </dl>
          {props.reference ? (
            <p className="PastDuePaymentZone__legend">
              {t('pastDuePayment.transfer.referenceHint')}
            </p>
          ) : null}
          <p className="PastDuePaymentZone__legend">
            {t('pastDuePayment.transfer.applies')}
          </p>
        </>
      ) : (
        <p className="PastDuePaymentZone__legend">
          {t('pastDuePayment.transfer.unavailable')}
        </p>
      )}
    </section>
  );
}

/**
 * One legal text per case, saying only what will happen (Fanny, 23-oct-2026):
 * on manual collection nothing is charged on its own (M-20), in a pause the
 * card pays the pause fee and then the plan (PP-04, PP-05), and otherwise the
 * plan's real period. Shared by Ajustes and the past-due notice's modal.
 */
export function usePastDueDisclosure(args: {
  view: tPastDuePaymentView;
  planName: string;
  /** «al mes», «cada 3 meses»… */
  frequency: string;
}): string {
  const { t } = useTranslation('settings/SubscriptionManager');
  if (args.view.isManualCollection)
    return t('pastDuePayment.disclosure.manual');
  if (args.view.isPauseFee) {
    return t('pastDuePayment.disclosure.pause', { plan: args.planName });
  }
  return t('pastDuePayment.disclosure.automatic', {
    plan: args.planName,
    frequency: args.frequency,
  });
}

/**
 * Layout C (Fanny, 21-oct-2026): the right column of «Detalles de tu
 * suscripción», in past due, for the Owner and the Admin (RO-1). One place that
 * answers how much I owe, with which card, what happened and what to do. Below
 * `md` it comes first, above the plan block (revision 1, B1).
 *
 * - «Lo que debes» and the amount (M-16: the button charges all of it).
 * - What the amount is made of, folded under it (M-16, Fanny, 23-oct).
 * - The saved cards, chosen right here, the refused one first; its decline
 *   lives inside its box, mark and reason in one line (M-21, Fanny, 23-oct).
 * - A decline with no saved card to sit in goes on top, before the cards.
 * - ONE primary button, whose label follows the selected card.
 * - The legend says before the click that paying with another card makes it
 *   the plan's card (M-21, MO-7).
 * - While the bank confirms, the button gives way to a status line and the
 *   list is locked, so nobody pays twice or retargets mid-charge (RET-2175).
 */
export default function PastDuePaymentZone(props: {
  view: tPastDuePaymentView;
  disclosure: string;
  /** For the breakdown's «Tu plan Profesional». */
  planName: string;
}): ReactElement {
  const { t } = useTranslation('settings/SubscriptionManager');
  const { view } = props;
  const radioName = useId();
  const titleId = useId();

  const decline = view.decline;
  const declinedId = declinedCardId(decline);
  const savedIds = new Set(view.paymentMethods.map((pm) => pm.paymentMethodId));
  const declineIsOnSavedCard = declinedId !== null && savedIds.has(declinedId);
  // The renewal's card may be gone (deleted since), and our own error points at
  // no card: the notice still says what happened, without a card to retry (M-21).
  const looseNotice =
    decline !== null && !declineIsOnSavedCard ? decline : null;
  // The refused card first: with many cards it would otherwise sit, with its
  // notice, under the fold (revision 1, M5).
  const cards = declineIsOnSavedCard
    ? [
        ...view.paymentMethods.filter(
          (pm) => pm.paymentMethodId === declinedId,
        ),
        ...view.paymentMethods.filter(
          (pm) => pm.paymentMethodId !== declinedId,
        ),
      ]
    : view.paymentMethods;

  const payButton = usePastDuePayButton(view);
  const isPartial = view.phase === 'partial' && view.partial !== null;
  const isConfirming = view.phase === 'confirming';
  // The retry goes to the plan's card (MO-5), which may be the refused one, so
  // the line says which card it is. Adding a card without paying moves it to
  // the new one (MO-7). With no plan card there is nothing to retry against,
  // so there is no line (Fanny, 23-oct-2026).
  const planCard = view.paymentMethods.find(
    (pm) => pm.paymentMethodId === view.planPaymentMethodId,
  );
  // The plan card's decline reason does not allow a retry: billing stops the
  // automatic retries too, so neither the line nor the chip shows (M-21,
  // Fanny, 23-oct-2026). The cap does not stop them: it counts only the
  // customer's own attempts (Fanny, 24-oct-2026).
  const planCardRetryStopped =
    planCard !== undefined &&
    decline !== null &&
    declinedCardId(decline) === planCard.paymentMethodId &&
    !decline.retryAllowed &&
    decline.retryLimit === null;
  const showRetry = Boolean(
    view.nextRetryDate && planCard && !planCardRetryStopped,
  );
  // No saved card can pay (refused without retry, or expired): adding a card
  // is the way out, as the primary action (M-21, 23 and 24-oct-2026).
  const onlyCardCannotRetry =
    view.paymentMethods.length > 0 &&
    view.paymentMethods.every(
      (pm) =>
        isExpiredCard(pm) ||
        (decline !== null &&
          declinedId === pm.paymentMethodId &&
          !decline.retryAllowed),
    );
  // Unless the payment link is the chosen option: then the button opens it,
  // with no saved card too, and «Agregar tarjeta» goes back under the options
  // (Fanny, 24-oct-2026).
  const addCardIsPrimary =
    (view.paymentMethods.length === 0 || onlyCardCannotRetry) &&
    view.selectedPaymentMethodId !== PAST_DUE_HOSTED_OPTION;
  const becomesPlanCard =
    view.planPaymentMethodId !== null &&
    view.selectedPaymentMethodId !== null &&
    view.selectedPaymentMethodId !== PAST_DUE_HOSTED_OPTION &&
    view.selectedPaymentMethodId !== view.planPaymentMethodId;

  // On manual collection the card section starts folded, unless a card
  // payment is already under way or was just refused.
  const [cardOpen, setCardOpen] = useState(
    view.phase !== 'idle' || decline !== null,
  );

  const cardPayment = (
    <>
      {looseNotice && !isConfirming ? (
        <DeclineNotice decline={looseNotice} partial={isPartial} namesCard />
      ) : null}

      <fieldset className="PastDuePaymentZone__cards" disabled={isConfirming}>
        <legend className="PastDuePaymentZone__cardsTitle">
          {t('pastDuePayment.payWith')}
        </legend>
        {/* One grid for every option, so the name, the expiry and the tags
            line up across the cards (Fanny, 24-oct-2026). */}
        <div className="PastDuePaymentZone__cardList">
          {cards.map((pm) => (
            <CardOption
              key={pm.paymentMethodId}
              paymentMethod={pm}
              name={radioName}
              checked={pm.paymentMethodId === view.selectedPaymentMethodId}
              disabled={isConfirming}
              decline={
                declinedId === pm.paymentMethodId && !isConfirming
                  ? decline
                  : null
              }
              partial={isPartial}
              onSelect={view.onSelectPaymentMethod}
            />
          ))}
          {/* One more option, like another card: «Pagar con un enlace de pago»,
            Stripe's own page for the invoice (Fanny, 23-oct-2026). Only with ONE open invoice:
            that page charges a single invoice (M-16). */}
          {view.hostedPaymentUrl ? (
            <div
              className={classNames('PastDuePaymentZone__cardBlock', {
                'PastDuePaymentZone__cardBlock--checked':
                  view.selectedPaymentMethodId === PAST_DUE_HOSTED_OPTION,
              })}
            >
              <label
                className="PastDuePaymentZone__card"
                data-testid="past-due-hosted-option"
              >
                <input
                  type="radio"
                  className="radio radio-primary radio-sm"
                  name={radioName}
                  value={PAST_DUE_HOSTED_OPTION}
                  checked={
                    view.selectedPaymentMethodId === PAST_DUE_HOSTED_OPTION
                  }
                  disabled={isConfirming}
                  onChange={() =>
                    view.onSelectPaymentMethod(PAST_DUE_HOSTED_OPTION)
                  }
                />
                <span
                  className="PastDuePaymentZone__cardBrand"
                  aria-hidden="true"
                >
                  <ArrowTopRightOnSquareIcon className="size-6" />
                </span>
                {/* No brand name: customers do not know Stripe (Fanny, 23-oct). */}
                <span className="PastDuePaymentZone__cardDigits PastDuePaymentZone__cardDigits--wide">
                  {t('pastDuePayment.hosted.option')}
                </span>
              </label>
            </div>
          ) : null}
        </div>
        {addCardIsPrimary ? null : (
          <Button
            color="ghost"
            size="sm"
            className="PastDuePaymentZone__addCard"
            onClick={view.onAddPaymentMethod}
            disabled={isConfirming}
          >
            {t('pastDuePayment.addCard')}
          </Button>
        )}
      </fieldset>

      <div className="PastDuePaymentZone__action">
        {addCardIsPrimary ? (
          // No saved card left, or the only one cannot be retried: adding one
          // is the way out, as the primary action (revision 3, M1; 23-oct).
          <Button color="primary" onClick={view.onAddPaymentMethod}>
            {t('pastDuePayment.addCard')}
          </Button>
        ) : view.phase === 'hostedOpened' ? (
          <>
            <p
              className="PastDuePaymentZone__confirming"
              role="status"
              data-testid="past-due-hosted-opened"
            >
              <span
                className="loading loading-spinner loading-sm"
                aria-hidden="true"
              />
              {t('pastDuePayment.hosted.opened')}
            </p>
            <Button color="ghost" size="sm" onClick={() => view.onPay()}>
              {t('pastDuePayment.hosted.reopen')}
            </Button>
          </>
        ) : isConfirming ? (
          <p
            className="PastDuePaymentZone__confirming"
            role="status"
            data-testid="past-due-confirming"
          >
            <span
              className="loading loading-spinner loading-sm"
              aria-hidden="true"
            />
            {t('pastDuePayment.confirming', { amount: view.amountOwed ?? '' })}
          </p>
        ) : (
          <Button
            color="primary"
            onClick={() => view.onPay()}
            disabled={payButton.disabled}
            data-testid="past-due-pay-button"
          >
            {payButton.label}
          </Button>
        )}
        {/* The legend describes a charge: not under a button that is off
            or while the charge is already being confirmed. */}
        {isConfirming ||
        view.phase === 'hostedOpened' ||
        payButton.disabled ||
        addCardIsPrimary ? null : (
          <p className="PastDuePaymentZone__legend">
            {/* On manual collection the plan keeps paying by transfer, so no
                legend speaks of «la tarjeta de tu plan»: the disclosure
                already says so (Fanny, 24-oct-2026). */}
            {view.selectedPaymentMethodId === PAST_DUE_HOSTED_OPTION
              ? t(
                  view.isManualCollection
                    ? 'pastDuePayment.hosted.legendManual'
                    : 'pastDuePayment.hosted.legend',
                )
              : becomesPlanCard && !view.isManualCollection
                ? t('pastDuePayment.legendBecomesPlanCard')
                : t('pastDuePayment.legend')}
          </p>
        )}
      </div>
      <p className="SubscriptionOverview__disclosure">{props.disclosure}</p>
    </>
  );

  // Paid, and the status not updated yet: no amount, no cards, no button.
  if (view.phase === 'settling') {
    return (
      <section
        className="PastDuePaymentZone"
        aria-labelledby={titleId}
        data-testid="past-due-payment-zone"
      >
        <div className="PastDuePaymentZone__summary">
          <h2 id={titleId} className="PastDuePaymentZone__title">
            {t('pastDuePayment.title')}
          </h2>
        </div>
        <AlertCard
          variant="success"
          icon={CheckCircleIcon}
          title={t('pastDuePayment.settling.title')}
          descriptions={[t('pastDuePayment.settling.body')]}
          role="status"
          className="PastDuePaymentZone__notice"
        />
      </section>
    );
  }

  if (view.phase === 'loading') {
    return (
      <section
        className="PastDuePaymentZone"
        aria-labelledby={titleId}
        aria-busy="true"
        data-testid="past-due-payment-zone"
      >
        <div className="PastDuePaymentZone__summary">
          <h2 id={titleId} className="PastDuePaymentZone__title">
            {t('pastDuePayment.title')}
          </h2>
          <div className="skeleton h-9 w-48" />
          <div className="skeleton h-4 w-64" />
        </div>
        <div className="PastDuePaymentZone__cards">
          <div className="skeleton h-14 w-full" />
          <div className="skeleton h-14 w-full" />
        </div>
      </section>
    );
  }

  return (
    <section
      className="PastDuePaymentZone"
      aria-labelledby={titleId}
      data-testid="past-due-payment-zone"
    >
      <div className="PastDuePaymentZone__summary">
        <h2 id={titleId} className="PastDuePaymentZone__title">
          {t('pastDuePayment.title')}
        </h2>
        {view.phase === 'amountUnavailable' ? null : (
          <div className="PastDuePaymentZone__amountRow">
            <p
              className="PastDuePaymentZone__amount"
              data-testid="subscription-amount-due"
            >
              {view.amountOwed}
            </p>
            {/* A retry is scheduled, not happening now: «programado», not «en
                curso», or the customer would wait instead of paying (Fanny,
                23-oct-2026). Only while Stripe has one to a plan card. */}
            {showRetry ? (
              <Chip
                color="default"
                variant="outlined"
                size="md"
                icon={<ArrowPathIcon className="size-4" aria-hidden="true" />}
                data-testid="past-due-retry-chip"
              >
                {t('pastDuePayment.retryScheduled')}
              </Chip>
            ) : null}
          </div>
        )}
        {view.isPauseFee && view.phase !== 'amountUnavailable' ? (
          <p className="PastDuePaymentZone__retry">
            {t('pastDuePayment.pauseFee')}
          </p>
        ) : null}
        {/* A partial charge is a fact about the amount, said next to it in
            grey, not a second warning box (Fanny, 23-oct). */}
        {isPartial && view.partial ? (
          <p className="PastDuePaymentZone__retry">
            {t('pastDuePayment.partialPaid', {
              paid: view.partial.paid,
              total: view.partial.total,
            })}
          </p>
        ) : null}
        {/* A transfer for less than the invoice waits as cash balance: say
            it arrived and what is missing (M-20, Fanny, 24-oct-2026). */}
        {view.isManualCollection &&
        view.transferReceived &&
        view.phase !== 'amountUnavailable' ? (
          <p
            className="PastDuePaymentZone__retry"
            data-testid="past-due-transfer-received"
          >
            {t('pastDuePayment.transfer.received', view.transferReceived)}
          </p>
        ) : null}
        {view.isManualCollection &&
        view.dueDate &&
        view.phase !== 'amountUnavailable' ? (
          // Manual collection has no automatic retry: what matters is the due
          // date, before it passes (M-20) and after.
          <p className="PastDuePaymentZone__retry">
            {view.dueDate.getTime() > Date.now()
              ? t('pastDuePayment.dueOn', {
                  date: formatDeclineDate(view.dueDate),
                })
              : t('pastDuePayment.dueWas', {
                  date: formatDeclineDate(view.dueDate),
                })}
          </p>
        ) : null}
        {showRetry &&
        planCard &&
        view.nextRetryDate &&
        view.phase !== 'amountUnavailable' ? (
          <p className="PastDuePaymentZone__retry">
            {t('pastDuePayment.nextRetryWithCard', {
              date: formatDeclineDate(view.nextRetryDate),
              brand: brandName(planCard.brand),
              last4: planCard.lastDigits,
            })}
          </p>
        ) : null}
        {view.phase === 'amountUnavailable' ? null : (
          <DebtBreakdown
            invoices={view.debtInvoices}
            planName={props.planName}
          />
        )}
      </div>

      {/* Under the amount, for card and transfer alike: it is about what is
          owed, not about the card (M-16, 23-oct-2026). */}
      {view.phase === 'debtChanged' ? (
        <AlertCard
          variant="warning"
          icon={ExclamationTriangleIcon}
          title={t('pastDuePayment.debtChanged.title')}
          descriptions={[t('pastDuePayment.debtChanged.body')]}
          role="status"
          className="PastDuePaymentZone__notice"
          dataAttributes={{ 'data-testid': 'past-due-debt-changed' }}
        />
      ) : null}

      {view.phase === 'amountUnavailable' ? (
        <AlertCard
          variant="warning"
          icon={ExclamationTriangleIcon}
          title={t('pastDuePayment.amountUnavailableTitle')}
          descriptions={[t('pastDuePayment.amountUnavailableBody')]}
          role="status"
          className="PastDuePaymentZone__notice"
        />
      ) : view.isManualCollection && view.transferSupported ? (
        <>
          <TransferBlock
            instructions={view.transferInstructions}
            amount={view.amountOwed}
            reference={
              view.debtInvoices.length === 1
                ? view.debtInvoices[0].number
                : null
            }
            received={view.transferReceived}
          />
          {/* The card stays as an alternative, folded: it settles the debt at
                once, and the plan keeps being billed by invoice (Fanny, 23-oct). */}
          <Button
            color="ghost"
            size="sm"
            className="PastDuePaymentZone__addCard"
            onClick={() => setCardOpen((value) => !value)}
            aria-expanded={cardOpen}
            data-testid="past-due-pay-by-card-toggle"
          >
            {t(
              cardOpen
                ? 'pastDuePayment.transfer.hideCard'
                : 'pastDuePayment.transfer.payByCard',
            )}
          </Button>
          {cardOpen ? cardPayment : null}
        </>
      ) : (
        cardPayment
      )}
    </section>
  );
}
